import { useRef, useState } from "react";
import { useEditorStore } from "@/store";
import { readStyleFile, fetchStyleUrl, downloadStyle } from "@/lib/fileIO";
import { transformForReactNative } from "@/lib/transform";
import { loadDefaultStyle } from "@/lib/defaultStyle";
import { useDarkMode } from "@/hooks/useDarkMode";
import { STYLE_PRESETS } from "@/lib/presets";

export function Toolbar() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [urlDialog, setUrlDialog] = useState(false);
  const [urlValue, setUrlValue] = useState("");
  const [urlLoading, setUrlLoading] = useState(false);
  const [urlError, setUrlError] = useState("");
  const [downloadDialog, setDownloadDialog] = useState(false);
  const [dlFilename, setDlFilename] = useState("style");
  const [dlWeb, setDlWeb] = useState(true);
  const [dlRN, setDlRN] = useState(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);

  const { style, undo, redo, undoStack, redoStack, validationErrors, toggleErrorPanel, showComparison, toggleComparison, loadNewStyle, hasUnsavedChanges } =
    useEditorStore();
  const { theme, cycle: cycleTheme } = useDarkMode();

  const errorCount = validationErrors.length;

  /** Wraps a destructive action with an unsaved-changes confirmation dialog. */
  const confirmIfDirty = (action: () => void) => {
    if (hasUnsavedChanges()) {
      setConfirmAction(() => action);
    } else {
      action();
    }
  };

  const handleNew = () => {
    confirmIfDirty(async () => {
      const s = await loadDefaultStyle();
      loadNewStyle(s);
    });
  };

  const handleOpenFile = () => {
    confirmIfDirty(() => {
      fileInputRef.current?.click();
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { style: loaded } = await readStyleFile(file);
      loadNewStyle(loaded);
    } catch (err) {
      alert(`Failed to load file: ${err instanceof Error ? err.message : String(err)}`);
    }
    e.target.value = "";
  };

  const openUrlDialog = () => {
    confirmIfDirty(() => {
      setUrlValue("");
      setUrlError("");
      setUrlLoading(false);
      setUrlDialog(true);
    });
  };

  const handleUrlSubmit = async () => {
    if (!urlValue.trim()) return;
    setUrlError("");
    setUrlLoading(true);
    try {
      const { style: loaded } = await fetchStyleUrl(urlValue.trim());
      loadNewStyle(loaded);
      setUrlDialog(false);
    } catch (err) {
      setUrlError(err instanceof Error ? err.message : String(err));
    } finally {
      setUrlLoading(false);
    }
  };

  const openDownloadDialog = () => {
    const name = (style.name as string) ?? "style";
    setDlFilename(name.replace(/[^a-zA-Z0-9_-]/g, "-").toLowerCase() || "style");
    setDownloadDialog(true);
  };

  const handleDownload = () => {
    const base = dlFilename.replace(/\.json$/, "") || "style";
    if (dlWeb) downloadStyle(style, `${base}.json`);
    if (dlRN) downloadStyle(transformForReactNative(style), `${base}.rn.json`);
    setDownloadDialog(false);
    // After download, update the original so hasUnsavedChanges resets
    useEditorStore.getState().loadNewStyle(style);
  };

  return (
    <>
      <header className="flex h-11 shrink-0 items-center gap-2 bg-surface-raised px-3 shadow-sm">
        <h1 className="mr-2 text-sm font-semibold text-text-primary tracking-tight">
          MapLibre Style Editor
        </h1>

        <div className="h-4 w-px bg-border-subtle" />

        <button type="button" onClick={handleNew} className="toolbar-btn" title="New style">
          New
        </button>
        <button type="button" onClick={handleOpenFile} className="toolbar-btn" title="Open file">
          Open
        </button>
        <button type="button" onClick={openUrlDialog} className="toolbar-btn" title="Open from URL">
          URL
        </button>

        <div className="h-4 w-px bg-border-subtle" />

        <button type="button" onClick={openDownloadDialog} className="toolbar-btn" title="Download style">
          ↓ Download
        </button>

        <div className="h-4 w-px bg-border-subtle" />

        <button
          type="button"
          onClick={undo}
          disabled={undoStack.length === 0}
          className="toolbar-btn disabled:opacity-30"
          title="Undo (Ctrl+Z)"
        >
          ↩
        </button>
        <button
          type="button"
          onClick={redo}
          disabled={redoStack.length === 0}
          className="toolbar-btn disabled:opacity-30"
          title="Redo (Ctrl+Shift+Z)"
        >
          ↪
        </button>

        <div className="flex-1" />

        <button
          type="button"
          onClick={toggleComparison}
          className={`toolbar-btn ${showComparison ? "text-accent" : ""}`}
          title="Before/after comparison"
        >
          ◧ Compare
        </button>

        <button
          type="button"
          onClick={cycleTheme}
          className="toolbar-btn text-sm"
          title={`Theme: ${theme}`}
        >
          {theme === "dark" ? "🌙" : theme === "light" ? "☀️" : "🖥"}
        </button>

        <button
          type="button"
          onClick={toggleErrorPanel}
          className={`flex items-center gap-1.5 rounded-radius-full px-2.5 py-1 text-xs font-medium transition-colors ${
            errorCount > 0
              ? "bg-error-muted text-error hover:bg-error-muted/80"
              : "bg-success/10 text-success"
          }`}
        >
          <span className={`inline-block h-1.5 w-1.5 rounded-full ${errorCount > 0 ? "bg-error" : "bg-success"}`} />
          {errorCount > 0 ? `${errorCount} error${errorCount > 1 ? "s" : ""}` : "Valid"}
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          className="hidden"
          onChange={handleFileChange}
        />
      </header>

      {/* Unsaved changes confirmation */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setConfirmAction(null)}>
          <div
            className="w-96 rounded-radius-lg border border-border-subtle bg-surface-raised px-6 py-5 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-sm font-semibold text-text-primary">Unsaved changes</h2>
            <p className="mt-2 text-xs text-text-secondary">
              You have unsaved changes that will be lost. Download your style first if you want to keep them.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmAction(null)}
                className="rounded-radius-sm px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-sunken"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const action = confirmAction;
                  setConfirmAction(null);
                  action();
                }}
                className="rounded-radius-sm border border-error/30 bg-error px-3 py-1.5 text-xs font-medium text-white hover:bg-error/90 transition-colors"
              >
                Discard changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* URL Dialog */}
      {urlDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setUrlDialog(false)}>
          <div
            className="w-[28rem] rounded-radius-lg border border-border-subtle bg-surface-raised px-6 py-5 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-sm font-semibold text-text-primary">Open style from URL</h2>
            <p className="mt-1 text-[11px] text-text-muted">
              Enter a URL or pick a preset. This replaces the current style.
            </p>

            <div className="mt-4">
              <input
                type="url"
                value={urlValue}
                onChange={(e) => setUrlValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleUrlSubmit(); }}
                placeholder="https://example.com/style.json"
                autoFocus
                className="h-8 w-full rounded-radius-sm border border-border-subtle bg-surface-base px-3 text-xs text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
              />
            </div>

            {/* Style presets */}
            <div className="mt-3 max-h-48 overflow-y-auto rounded-radius-sm border border-border-subtle">
              {STYLE_PRESETS.map((preset) => (
                <button
                  key={preset.url}
                  type="button"
                  onClick={() => setUrlValue(preset.url)}
                  className={`flex w-full items-start gap-2 border-b border-border-subtle px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-surface-sunken ${
                    urlValue === preset.url ? "bg-accent-muted" : ""
                  }`}
                >
                  <div className="flex-1">
                    <div className="text-[11px] font-medium text-text-primary">{preset.name}</div>
                    <div className="text-[9px] text-text-muted">{preset.description}</div>
                  </div>
                  <span className="shrink-0 rounded-radius-full bg-surface-sunken px-1.5 py-0.5 text-[8px] text-text-muted">
                    {preset.coverage}
                  </span>
                </button>
              ))}
            </div>

            {urlError && (
              <div className="mt-3 rounded-radius-sm bg-error-muted px-3 py-2 text-xs text-error">
                {urlError}
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setUrlDialog(false)}
                className="rounded-radius-sm px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-sunken"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUrlSubmit}
                disabled={!urlValue.trim() || urlLoading}
                className="rounded-radius-sm bg-accent px-3 py-1.5 text-xs font-medium text-accent-text disabled:opacity-40 hover:bg-accent-hover transition-colors"
              >
                {urlLoading ? "Loading..." : "Open"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Download Dialog */}
      {downloadDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setDownloadDialog(false)}>
          <div
            className="w-96 rounded-radius-lg border border-border-subtle bg-surface-raised px-6 py-5 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-sm font-semibold text-text-primary">Download style</h2>

            <div className="mt-4">
              <label className="mb-1 block text-[10px] font-medium text-text-secondary">Filename</label>
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={dlFilename}
                  onChange={(e) => setDlFilename(e.target.value)}
                  autoFocus
                  className="h-8 flex-1 rounded-radius-sm border border-border-subtle bg-surface-base px-3 text-xs text-text-primary focus:border-accent focus:outline-none"
                />
                <span className="text-xs text-text-muted">.json</span>
              </div>
            </div>

            <div className="mt-4">
              <label className="mb-2 block text-[10px] font-medium text-text-secondary">Formats</label>
              <div className="space-y-2">
                <label className="flex items-start gap-2.5 rounded-radius-sm border border-border-subtle p-2.5 cursor-pointer hover:bg-surface-sunken transition-colors">
                  <input
                    type="checkbox"
                    checked={dlWeb}
                    onChange={(e) => setDlWeb(e.target.checked)}
                    className="mt-0.5 accent-accent"
                  />
                  <div>
                    <div className="text-xs font-medium text-text-primary">Web (standard)</div>
                    <div className="text-[10px] text-text-muted">
                      Standard MapLibre style.json with kebab-case properties.
                      Saves as <span className="font-mono">{dlFilename || "style"}.json</span>
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-2.5 rounded-radius-sm border border-border-subtle p-2.5 cursor-pointer hover:bg-surface-sunken transition-colors">
                  <input
                    type="checkbox"
                    checked={dlRN}
                    onChange={(e) => setDlRN(e.target.checked)}
                    className="mt-0.5 accent-accent"
                  />
                  <div>
                    <div className="text-xs font-medium text-text-primary">React Native</div>
                    <div className="text-[10px] text-text-muted">
                      camelCase properties for @maplibre/maplibre-react-native.
                      Saves as <span className="font-mono">{dlFilename || "style"}.rn.json</span>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDownloadDialog(false)}
                className="rounded-radius-sm px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-sunken"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDownload}
                disabled={!dlWeb && !dlRN}
                className="rounded-radius-sm bg-accent px-3 py-1.5 text-xs font-medium text-accent-text disabled:opacity-40 hover:bg-accent-hover transition-colors"
              >
                Download{dlWeb && dlRN ? " both" : ""}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
